import React, { useEffect, useState } from 'react';
import { SourceService } from '../../../../services/v1/applicant_tracking/SourceService';
import { PageHeader } from '../../../survey_engine/SurveyTemplates/PageHeader';
import { Card } from '../../../../components/Card';
import { CardHeader } from '../../../survey_engine/SurveyTemplates/CardHeader';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { ChannelTypeInput } from '../components/ChannelTypeInput';
import { FormHeader } from '../components/FormHeader';
import { Alert, AlertType } from '../../../../components/Alert';
import { IconSpan } from '../../../../components/IconSpan';
import classNames from 'classnames';
import styles from '../styles.module.scss';
import { ChannelType } from '../../../../entities/applicant_tracking/Source';
import { SourcesInput } from '../components/SourcesInput';
import {
  DUPLICATES_FOUND_ALERT_MESSAGE,
  REDIRECT_URI,
  SOURCE_NAME_MAX_CHARACTERS,
  SUCCESSFUL_ALERT_UPDATE_MESSAGE,
  SourceData,
  createSourceData,
} from '../util';
import { redirectWithAlert } from '../../../../services/FlashService';

interface PropTypes {
  id: number;
  name: string;
  channelType: ChannelType;
  enabledOnCareers: boolean;
}

export default function EditSourcePage(props: PropTypes) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertActive, setAlertActive] = useState<boolean>(false);
  const [channelType, setChannelType] = useState<ChannelType>(
    props.channelType,
  );
  const [sources, setSources] = useState<SourceData[]>([
    createSourceData(props.name, props.enabledOnCareers),
  ]);
  const [duplicates, setDuplicates] = useState<SourceData[]>([]);
  useEffect(() => setDuplicates([]), [channelType]);

  function isSavable() {
    const sourceNames = sources.map((source) => source.name);

    return (
      sources.length > 0 &&
      sources.every((source) => source.name.length > 0) &&
      duplicates.every((duplicate) => !sourceNames.includes(duplicate.name)) &&
      channelType !== null
    );
  }

  async function handleSave() {
    setIsLoading(true);
    const source = sources[0];

    try {
      await SourceService.update(
        props.id,
        source.name.trim(),
        source.enabledOnCareers,
        channelType,
      );
      redirectWithAlert(REDIRECT_URI, SUCCESSFUL_ALERT_UPDATE_MESSAGE);
    } catch (error) {
      if (error.status !== 422) throw error;

      setDuplicates([createSourceData(source.name, source.enabledOnCareers)]);
      setAlertActive(true);
      setIsLoading(false);
    }
  }

  return (
    <>
      {isLoading && <LoadingSpinner showBackdrop />}
      {alertActive && (
        <Alert
          type={AlertType.Danger}
          clearable
          autoClearTimeout={4000}
          onClose={() => setAlertActive(false)}
        >
          <IconSpan
            spanText={DUPLICATES_FOUND_ALERT_MESSAGE}
            icon={{ name: 'bi-info-circle' }}
            spanClassName='ms-3'
          />
        </Alert>
      )}
      <div className='container'>
        <PageHeader title={props.name} backUrl={REDIRECT_URI} />
        <Card className={classNames(styles['card'])}>
          <CardHeader
            title='Sources'
            isSavable={isSavable()}
            onSaveClick={handleSave}
            cancelUrl={REDIRECT_URI}
          />
          <ChannelTypeInput
            channelType={channelType}
            setChannelType={setChannelType}
          />
          <FormHeader
            mandatory
            title='Source'
            constraint={`${sources[0].name.length}/${SOURCE_NAME_MAX_CHARACTERS}`}
          />
          <SourcesInput
            singleInput
            sources={sources}
            duplicates={duplicates}
            setSources={setSources}
          />
        </Card>
      </div>
    </>
  );
}
